import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
  forwardRef,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { IconComponentV2 } from '../../icon-component/icon.component';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);

const LA_TIMEZONE = 'America/Los_Angeles';
const nowInLA = () => dayjs().tz(LA_TIMEZONE);

export type SelectionMode = 'single' | 'multiple' | 'date-picker';
export type SelectionSize = 'sm' | 'md' | 'lg';
export type SelectionVariant = 'default' | 'card' | 'button';

export interface SelectionOption<T = any> {
  value: T;
  label: string;
  disabled?: boolean;
  description?: string;
}

type CalendarView = 'days' | 'months' | 'years';

interface DatePickerDay {
  date: dayjs.Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

const YEAR_GRID_SIZE = 12;

@Component({
  selector: 'app-selection',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, ClickOutsideDirective],
  templateUrl: './selection.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectionComponent),
      multi: true,
    },
  ],
})
export class SelectionComponent<T = any> implements ControlValueAccessor, OnChanges {
  @Input() mode: SelectionMode = 'single';
  @Input() options: SelectionOption<T>[] = [];
  @Input() name = '';
  @Input() disabled = false;
  @Input() required = false;

  // Appearance
  @Input() size: SelectionSize = 'md';
  @Input() variant: SelectionVariant = 'default';
  @Input() orientation: 'vertical' | 'horizontal' = 'vertical';
  @Input() fullWidth = true;
  @Input() themeColor?: string;

  // Label and Validation
  @Input() label?: string;
  @Input() hint?: string;
  @Input() error?: string;

  // Custom Classes
  @Input() containerClass?: string;
  @Input() labelClass?: string;
  @Input() optionClass?: string;

  // Date-picker mode
  @Input() minDate?: string;
  @Input() maxDate?: string;
  @Input() datePlaceholder = 'Select date';
  @Input() triggerClass?: string;
  @Input() enableYearMonthPicker = false;
  @Input() disableFutureDates = false;
  @Input() disableWeekends = false;

  // Events
  @Output() valueChange = new EventEmitter<T | T[]>();
  @Output() selectionChange = new EventEmitter<SelectionOption<T> | SelectionOption<T>[]>();

  // Internal State
  selectedValue = signal<T | T[] | null>(null);
  readonly calendarOpen = signal(false);
  readonly calendarMonth = signal(nowInLA().startOf('month'));
  readonly calendarView = signal<CalendarView>('days');
  readonly yearRangeStart = signal(nowInLA().year() - 5);

  readonly weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  readonly monthShortNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  readonly monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  readonly calendarDays = computed(() => this.buildCalendarDays(this.calendarMonth()));

  // ControlValueAccessor
  private onChange = (value: any) => {};
  private onTouched = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mode !== 'date-picker') return;
    if (changes['minDate'] || changes['maxDate'] || changes['disableFutureDates'] || changes['disableWeekends']) {
      this.validateAndClearInvalidValue();
    }
  }

  // Public Methods
  onOptionChange(option: SelectionOption<T>): void {
    if (this.mode === 'date-picker') return;
    if (option.disabled || this.disabled) return;

    this.onTouched();

    if (this.mode === 'single') {
      this.selectedValue.set(option.value);
      this.onChange(option.value);
      this.valueChange.emit(option.value);
      this.selectionChange.emit(option);
    } else {
      const currentValue = Array.isArray(this.selectedValue()) ? (this.selectedValue() as T[]) : [];
      const index = currentValue.findIndex((v) => this.compareValues(v, option.value));

      let newValue: T[];
      if (index > -1) {
        newValue = currentValue.filter((_, i) => i !== index);
      } else {
        newValue = [...currentValue, option.value];
      }

      this.selectedValue.set(newValue);
      this.onChange(newValue);
      this.valueChange.emit(newValue);

      const selectedOptions = this.options.filter((opt) =>
        newValue.some((v) => this.compareValues(v, opt.value))
      );
      this.selectionChange.emit(selectedOptions);
    }
  }

  toggleCalendar(): void {
    if (this.disabled) return;
    const next = !this.calendarOpen();
    if (next) {
      const selected = this.getSelectedDate();
      this.calendarMonth.set((selected ?? nowInLA()).startOf('month'));
      this.calendarView.set('days');
      this.syncYearRangeStart(this.calendarMonth().year());
    }
    this.calendarOpen.set(next);
    if (next) {
      this.onTouched();
    }
  }

  closeCalendar(): void {
    this.calendarOpen.set(false);
    this.calendarView.set('days');
  }

  navigatePrev(): void {
    if (!this.canGoPrev()) return;
    const view = this.calendarView();
    if (view === 'days') {
      this.calendarMonth.set(this.calendarMonth().subtract(1, 'month'));
    } else if (view === 'months') {
      this.calendarMonth.set(this.calendarMonth().subtract(1, 'year'));
    } else {
      this.yearRangeStart.update((y) => Math.max(this.getMinYear(), y - YEAR_GRID_SIZE));
    }
  }

  navigateNext(): void {
    if (!this.canGoNext()) return;
    const view = this.calendarView();
    if (view === 'days') {
      this.calendarMonth.set(this.calendarMonth().add(1, 'month'));
    } else if (view === 'months') {
      this.calendarMonth.set(this.calendarMonth().add(1, 'year'));
    } else {
      const maxStart = this.getMaxYear() - YEAR_GRID_SIZE + 1;
      this.yearRangeStart.update((y) => Math.min(maxStart, y + YEAR_GRID_SIZE));
    }
  }

  openMonthView(): void {
    if (!this.enableYearMonthPicker) return;
    this.calendarView.set('months');
  }

  openYearView(): void {
    if (!this.enableYearMonthPicker) return;
    this.syncYearRangeStart(this.calendarMonth().year());
    this.calendarView.set('years');
  }

  onHeaderClick(): void {
    if (!this.enableYearMonthPicker) return;
    const view = this.calendarView();
    if (view === 'days') {
      this.openMonthView();
    } else if (view === 'months') {
      this.openYearView();
    }
  }

  selectMonth(monthIndex: number): void {
    if (this.isMonthDisabled(monthIndex)) return;
    this.calendarMonth.set(this.calendarMonth().month(monthIndex).startOf('month'));
    this.calendarView.set('days');
  }

  selectYear(year: number): void {
    if (this.isYearDisabled(year)) return;
    this.calendarMonth.set(this.calendarMonth().year(year).startOf('month'));
    this.calendarView.set('months');
  }

  yearGridYears(): number[] {
    const start = this.yearRangeStart();
    return Array.from({ length: YEAR_GRID_SIZE }, (_, i) => start + i);
  }

  yearRangeEnd(): number {
    return this.yearRangeStart() + YEAR_GRID_SIZE - 1;
  }

  getCalendarHeaderLabel(): string {
    const view = this.calendarView();
    const month = this.calendarMonth();
    if (view === 'days') {
      return `${this.monthNames[month.month()]} ${month.year()}`;
    }
    if (view === 'months') {
      return String(month.year());
    }
    return `${this.yearRangeStart()} – ${this.yearRangeEnd()}`;
  }

  isHeaderClickable(): boolean {
    return this.enableYearMonthPicker && this.calendarView() !== 'years';
  }

  canGoPrev(): boolean {
    const view = this.calendarView();
    if (view === 'days') {
      const min = this.getEffectiveMinDate();
      if (!min) return true;
      return this.calendarMonth().subtract(1, 'month').endOf('month').isSameOrAfter(min, 'day');
    }
    if (view === 'months') {
      return this.calendarMonth().year() > this.getMinYear();
    }
    return this.yearRangeStart() > this.getMinYear();
  }

  canGoNext(): boolean {
    const view = this.calendarView();
    if (view === 'days') {
      const max = this.getEffectiveMaxDate();
      if (!max) return true;
      return this.calendarMonth().add(1, 'month').startOf('month').isSameOrBefore(max, 'day');
    }
    if (view === 'months') {
      return this.calendarMonth().year() < this.getMaxYear();
    }
    return this.yearRangeStart() + YEAR_GRID_SIZE <= this.getMaxYear();
  }

  isMonthDisabled(monthIndex: number): boolean {
    const year = this.calendarMonth().year();
    const monthStart = dayjs.tz(`${year}-${String(monthIndex + 1).padStart(2, '0')}-01`, LA_TIMEZONE).startOf('month');
    const monthEnd = monthStart.endOf('month');
    const min = this.getEffectiveMinDate();
    const max = this.getEffectiveMaxDate();
    if (min && monthEnd.isBefore(min, 'day')) return true;
    if (max && monthStart.isAfter(max, 'day')) return true;
    return false;
  }

  isYearDisabled(year: number): boolean {
    const yearStart = dayjs.tz(`${year}-01-01`, LA_TIMEZONE).startOf('year');
    const yearEnd = dayjs.tz(`${year}-12-31`, LA_TIMEZONE).endOf('year');
    const min = this.getEffectiveMinDate();
    const max = this.getEffectiveMaxDate();
    if (min && yearEnd.isBefore(min, 'day')) return true;
    if (max && yearStart.isAfter(max, 'day')) return true;
    return false;
  }

  isCurrentMonthSelected(monthIndex: number): boolean {
    const selected = this.getSelectedDate();
    const month = this.calendarMonth();
    return !!selected && selected.year() === month.year() && selected.month() === monthIndex;
  }

  isCurrentYearSelected(year: number): boolean {
    return this.calendarMonth().year() === year;
  }

  getNavButtonClasses(canNavigate: boolean): string {
    const base = 'inline-flex h-8 w-8 items-center justify-center rounded-[8px] transition';
    if (canNavigate) {
      return `${base} text-[#7d7d93] hover:bg-[#f8f8f8] dark:text-ink-muted dark:hover:bg-surface-3`;
    }
    return `${base} cursor-not-allowed opacity-30 text-[#c4c4d4] dark:text-ink-subtle`;
  }

  getPickerCellClasses(isDisabled: boolean, isSelected: boolean): string {
    const base = 'flex h-9 items-center justify-center rounded-[8px] text-[13px] transition';
    if (isDisabled) {
      return `${base} cursor-not-allowed text-[#c4c4d4] dark:text-ink-subtle`;
    }
    if (isSelected) {
      return `${base} ${this.themeColor ? '' : 'bg-[#ad46ff]'} font-semibold text-white`;
    }
    return `${base} text-[#38385b] hover:bg-slate-100 dark:text-ink-soft dark:hover:bg-white/10`;
  }

  getHeaderButtonClasses(): string {
    const base =
      'text-[14px] font-semibold text-[#141437] dark:text-white';
    if (this.isHeaderClickable()) {
      return `${base} inline-flex items-center gap-1 rounded-[8px] px-2 py-1 transition hover:bg-[#f8f8f8] dark:hover:bg-surface-3`;
    }
    return base;
  }

  selectDate(day: DatePickerDay): void {
    if (day.isDisabled || this.disabled) return;
    const value = this.toLaDateString(day.date) as T;
    this.selectedValue.set(value);
    this.onChange(value);
    this.onTouched();
    this.valueChange.emit(value);
    this.calendarOpen.set(false);
  }

  displayDate(): string {
    const raw = this.selectedValue();
    if (raw == null || raw === '') return '';
    return this.formatDisplayDate(String(raw));
  }

  getDateTriggerClasses(): string {
    const base =
      'inline-flex h-[38px] w-full items-center justify-between gap-2 rounded-[10px] border border-[#d1d5dc] bg-white px-3 text-left text-[14px] text-[#38385b] outline-none transition focus:border-[#ad46ff] focus:ring-1 focus:ring-[#ad46ff]/30 dark:border-surface-3 dark:bg-surface-0 dark:text-ink-soft';
    const stateClasses = this.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#f8f8f8] dark:hover:bg-surface-3';
    const customClasses = this.triggerClass || '';
    return [base, stateClasses, customClasses].filter(Boolean).join(' ').trim();
  }

  getCalendarDayClasses(day: DatePickerDay): string {
    const classes = [
      'flex h-8 w-8 items-center justify-center rounded-full text-[13px] transition',
    ];

    if (day.isDisabled) {
      classes.push('cursor-not-allowed text-[#c4c4d4] dark:text-ink-subtle');
    } else if (day.isSelected) {
      classes.push(this.themeColor ? 'font-semibold text-white' : 'bg-[#ad46ff] font-semibold text-white');
    } else if (day.isToday) {
      classes.push(this.themeColor ? 'font-semibold hover:bg-slate-100' : 'font-semibold text-[#ad46ff] hover:bg-[#f4e5ff] dark:hover:bg-white/10');
    } else if (day.isCurrentMonth) {
      classes.push('text-[#38385b] hover:bg-slate-100 dark:text-ink-soft dark:hover:bg-white/10');
    } else {
      classes.push('text-[#a8a8c5] hover:bg-[#f8f8f8] dark:text-ink-subtle dark:hover:bg-surface-3');
    }

    return classes.join(' ');
  }

  isSelected(option: SelectionOption<T>): boolean {
    const currentValue = this.selectedValue();
    if (!currentValue) return false;

    if (this.mode === 'single') {
      return this.compareValues(currentValue as T, option.value);
    } else {
      const valuesArray = Array.isArray(currentValue) ? currentValue : [currentValue];
      return valuesArray.some((v) => this.compareValues(v, option.value));
    }
  }

  private compareValues(a: T, b: T): boolean {
    if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return a === b;
  }

  private buildCalendarDays(month: dayjs.Dayjs): DatePickerDay[] {
    const days: DatePickerDay[] = [];
    const startOfMonth = month.startOf('month');
    const endOfMonth = month.endOf('month');
    const startOfCalendar = startOfMonth.startOf('isoWeek').startOf('day');
    const endOfCalendar = endOfMonth.endOf('isoWeek').startOf('day');
    const selected = this.getSelectedDate();
    const today = nowInLA().startOf('day');

    let current = startOfCalendar;
    while (current.isSameOrBefore(endOfCalendar, 'day')) {
      const normalized = current.startOf('day');
      days.push({
        date: normalized,
        isCurrentMonth: normalized.isSame(month, 'month'),
        isToday: normalized.isSame(today, 'day'),
        isSelected: !!selected && normalized.isSame(selected, 'day'),
        isDisabled: this.isDateDisabled(normalized),
      });
      current = current.add(1, 'day');
    }

    return days;
  }

  private getSelectedDate(): dayjs.Dayjs | null {
    const raw = this.selectedValue();
    if (raw == null || raw === '') return null;
    const parsed = dayjs.tz(String(raw), LA_TIMEZONE);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }

  private getEffectiveMinDate(): dayjs.Dayjs | null {
    if (!this.minDate) return null;
    const min = dayjs.tz(this.minDate, LA_TIMEZONE).startOf('day');
    return min.isValid() ? min : null;
  }

  private getEffectiveMaxDate(): dayjs.Dayjs | null {
    const today = nowInLA().startOf('day');
    let max: dayjs.Dayjs | null = null;

    if (this.maxDate) {
      const parsed = dayjs.tz(this.maxDate, LA_TIMEZONE).startOf('day');
      if (parsed.isValid()) max = parsed;
    }

    if (this.disableFutureDates) {
      if (!max || max.isAfter(today, 'day')) {
        max = today;
      }
    }

    return max;
  }

  private getMinYear(): number {
    return this.getEffectiveMinDate()?.year() ?? 2017;
  }

  private getMaxYear(): number {
    return this.getEffectiveMaxDate()?.year() ?? nowInLA().year();
  }

  private syncYearRangeStart(targetYear: number): void {
    const minYear = this.getMinYear();
    const maxYear = this.getMaxYear();
    const maxStart = Math.max(minYear, maxYear - YEAR_GRID_SIZE + 1);
    let start = targetYear - Math.floor(YEAR_GRID_SIZE / 2);
    start = Math.max(minYear, Math.min(start, maxStart));
    this.yearRangeStart.set(start);
  }

  private validateAndClearInvalidValue(): void {
    const selected = this.getSelectedDate();
    if (!selected) return;
    if (!this.isDateDisabled(selected)) return;
    const empty = '' as T;
    this.selectedValue.set(empty);
    this.onChange(empty);
    this.valueChange.emit(empty);
  }

  private isDateDisabled(date: dayjs.Dayjs): boolean {
    const min = this.getEffectiveMinDate();
    if (min && date.isBefore(min, 'day')) return true;
    const max = this.getEffectiveMaxDate();
    if (max && date.isAfter(max, 'day')) return true;
    if (this.disableWeekends && (date.day() === 0 || date.day() === 6)) return true;
    return false;
  }

  private toLaDateString(date: dayjs.Dayjs): string {
    return date.tz(LA_TIMEZONE).format('YYYY-MM-DD');
  }

  private formatDisplayDate(value: string): string {
    const parsed = dayjs.tz(value, LA_TIMEZONE);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
  }

  private syncCalendarMonthFromValue(value: unknown): void {
    if (value == null || value === '') {
      this.calendarMonth.set(nowInLA().startOf('month'));
      return;
    }
    const parsed = dayjs.tz(String(value), LA_TIMEZONE);
    if (parsed.isValid()) {
      this.calendarMonth.set(parsed.startOf('month'));
    }
  }

  // ControlValueAccessor Implementation
  writeValue(value: T | T[] | null): void {
    if (this.mode === 'date-picker') {
      const nextValue = (value ?? '') as T | T[];
      this.selectedValue.set(nextValue);
      this.syncCalendarMonthFromValue(value);
      this.validateAndClearInvalidValue();
      return;
    }

    if (this.mode === 'multiple' && !Array.isArray(value)) {
      this.selectedValue.set(value ? [value] : []);
    } else {
      this.selectedValue.set(value);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.calendarOpen.set(false);
    }
  }

  // Styling Methods
  getContainerClasses(): string {
    const baseClasses = 'selection-container';
    const widthClasses = this.fullWidth ? 'w-full' : '';
    const customClasses = this.containerClass || '';

    return [baseClasses, widthClasses, customClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getLabelClasses(): string {
    const baseClasses = 'block mb-1 text-sm font-medium';
    const colorClasses = 'text-slate-700 dark:text-slate-300';
    const customClasses = this.labelClass || '';

    return [baseClasses, colorClasses, customClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getOptionsWrapperClasses(): string {
    const baseClasses = 'flex';
    const orientationClasses =
      this.orientation === 'vertical' ? 'flex-col space-y-2' : 'flex-row flex-wrap gap-3';
    const sizeClasses = this.getSizeSpacing();

    return [baseClasses, orientationClasses, sizeClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getOptionClasses(option: SelectionOption<T>): string {
    const isSelected = this.isSelected(option);
    const isDisabled = option.disabled || this.disabled;

    const baseClasses = 'relative flex items-start transition-all duration-200';
    const variantClasses = this.getVariantClasses(isSelected);
    const sizeClasses = this.getSizeClasses();
    const disabledClasses = isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
    const errorClasses = this.error ? 'border-red-300 dark:border-red-700' : '';
    const customClasses = this.optionClass || '';

    return [
      baseClasses,
      variantClasses,
      sizeClasses,
      disabledClasses,
      errorClasses,
      customClasses,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  private getVariantClasses(isSelected: boolean): string {
    if (this.variant === 'card') {
      return isSelected
        ? 'p-4 rounded-lg border-2 border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
        : 'p-4 rounded-lg border-2 border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600 bg-white dark:bg-slate-800';
    } else if (this.variant === 'button') {
      return isSelected
        ? 'px-4 py-2 rounded-lg bg-purple-500 text-white'
        : 'px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200';
    } else {
      return isSelected
        ? 'p-2 mb-0 rounded hover:bg-purple-50 dark:hover:bg-white/5'
        : 'p-2 mb-0 rounded hover:bg-slate-100 dark:hover:bg-slate-700/50';
    }
  }

  private getSizeClasses(): string {
    const sizeMap = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };
    return sizeMap[this.size];
  }

  private getSizeSpacing(): string {
    const sizeMap = {
      sm: 'gap-1.5',
      md: 'gap-2',
      lg: 'gap-3',
    };
    return sizeMap[this.size];
  }

  getInputClasses(): string {
    return 'peer sr-only';
  }

  getCustomInputClasses(isSelected: boolean): string {
    const baseClasses =
      'flex items-center justify-center rounded-full border-2 transition-all duration-200';
    const sizeClasses = this.getCustomInputSizeClasses();

    if (this.mode === 'single') {
      const stateClasses = isSelected
        ? 'border-purple-500 bg-white dark:bg-slate-800'
        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800';
      return [baseClasses, sizeClasses, stateClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    } else {
      const roundedClasses = 'rounded-md';
      const stateClasses = isSelected
        ? 'border-purple-500 bg-purple-500'
        : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800';
      return [baseClasses, roundedClasses, sizeClasses, stateClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    }
  }

  private getCustomInputSizeClasses(): string {
    const sizeMap = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };
    return sizeMap[this.size];
  }

  getCustomInputInnerClasses(isSelected: boolean): string {
    if (!isSelected) return 'hidden';

    const baseClasses = 'rounded-full transition-all duration-200';

    if (this.mode === 'single') {
      const sizeClasses = this.getRadioInnerSizeClasses();
      const colorClasses = 'bg-purple-500';
      return [baseClasses, sizeClasses, colorClasses]
        .filter(Boolean)
        .join(' ')
        .trim()
        .replace(/\s+/g, ' ');
    } else {
      return 'text-white text-xs font-bold';
    }
  }

  private getRadioInnerSizeClasses(): string {
    const sizeMap = {
      sm: 'w-2 h-2',
      md: 'w-2.5 h-2.5',
      lg: 'w-3 h-3',
    };
    return sizeMap[this.size];
  }

  getLabelTextClasses(option: SelectionOption<T>): string {
    const isSelected = this.isSelected(option);
    const baseClasses = 'font-medium transition-colors duration-200';
    const colorClasses = isSelected
      ? 'text-purple-600 dark:text-purple-400'
      : 'text-slate-700 dark:text-slate-200';

    return [baseClasses, colorClasses]
      .filter(Boolean)
      .join(' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  getDescriptionClasses(): string {
    return 'text-xs text-slate-500 dark:text-slate-400 mt-0.5';
  }

  getHintClasses(): string {
    return 'mt-1.5 text-xs text-slate-500 dark:text-slate-400';
  }

  getErrorClasses(): string {
    return 'mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1';
  }
}
